import React, { useState, useRef, useEffect } from 'react';
import { mockTables, businessEntityTypes } from '../../data/mockData';
// import ColumnSemantics from './ColumnSemantics'; // Commented out - Table/Column mapping disabled

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SemanticModel = () => {
  // Sub-tab state - Default to business-context
  const [activeSubTab, setActiveSubTab] = useState('business-context');
  
  // State for tables (commented out - Table mapping disabled)
  const [tables, setTables] = useState(mockTables);
  const [selectedTable, setSelectedTable] = useState(null);
  
  // State for form
  const [formData, setFormData] = useState({
    entityType: '',
    description: ''
  });
  
  // State for filters
  const [filterStatus, setFilterStatus] = useState('all'); // all, mapped, not_mapped
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  // State for Business Context tab
  const [businessContextText, setBusinessContextText] = useState('');
  const [businessContextFile, setBusinessContextFile] = useState(null);
  const businessContextFileRef = useRef(null);
  const [businessContextList, setBusinessContextList] = useState([]);
  const [syncingId, setSyncingId] = useState(null); // id being synced (row or null for global)
  const [deletingId, setDeletingId] = useState(null);

  // State for SQL Training tab
  const [sqlTrainingText, setSqlTrainingText] = useState('');
  const [sqlTrainingFile, setSqlTrainingFile] = useState(null);
  const sqlTrainingFileRef = useRef(null);
  const [sqlTrainingList, setSqlTrainingList] = useState([]);
  const [sqlSyncingId, setSqlSyncingId] = useState(null);
  const [sqlDeletingId, setSqlDeletingId] = useState(null);

  // Loading state for submissions
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Modal state for upload/write
  const [showBusinessContextUploadModal, setShowBusinessContextUploadModal] = useState(false);
  const [showSqlTrainingUploadModal, setShowSqlTrainingUploadModal] = useState(false);

  // Fetch business context list
  const fetchBusinessContexts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/training/business-context`);
      const result = await response.json();
      if (response.ok && result.success) {
        setBusinessContextList(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching business contexts:', error);
    }
  };

  // Show toast message
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  // Handle Business Context Sync (global - sync all where is_sync=0)
  const handleBusinessContextSync = async () => {
    setIsSyncing(true);
    setSyncingId('global');
    try {
      const response = await fetch(`${API_URL}/api/training/sync/business-context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(result.message || `✓ Synced ${result.trained_count || 0} chunks`, 'success');
        fetchBusinessContexts();
      } else {
        showToastMessage(result.message || result.detail || 'Sync failed', 'error');
      }
    } catch (error) {
      console.error('Error syncing business context:', error);
      showToastMessage('Error syncing business context. Please try again.', 'error');
    } finally {
      setIsSyncing(false);
      setSyncingId(null);
    }
  };

  // Handle row-level sync (single document)
  const handleSyncRow = async (id) => {
    setSyncingId(id);
    try {
      const response = await fetch(`${API_URL}/api/training/sync/business-context/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showToastMessage(result.message || 'Document synced', 'success');
        fetchBusinessContexts();
      } else {
        showToastMessage(result.message || result.detail || 'Sync failed', 'error');
      }
    } catch (error) {
      console.error('Error syncing row:', error);
      showToastMessage('Error syncing document.', 'error');
    } finally {
      setSyncingId(null);
    }
  };

  // Handle delete business context
  const handleDeleteBusinessContext = async (id) => {
    if (!window.confirm('Delete this document? Vanna will retrain with remaining documents in the background.')) return;
    setDeletingId(id);
    try {
      const response = await fetch(`${API_URL}/api/training/business-context/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showToastMessage(result.message || 'Document deleted.', 'success');
        setBusinessContextList((prev) => prev.filter((item) => item.id !== id));
      } else {
        showToastMessage(result.detail || result.message || 'Delete failed', 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showToastMessage('Error deleting document.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  // Poll SQL training list until all synced or timeout (for background sync)
  const pollSqlTrainingUntilSynced = async (maxAttempts = 30, intervalMs = 2000) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const response = await fetch(`${API_URL}/api/training/sql-training`);
      const result = await response.json();
      if (response.ok && result.success && result.data) {
        setSqlTrainingList(result.data);
        const allSynced = result.data.every((item) => item.is_sync === 1);
        if (allSynced || result.data.length === 0) break;
      }
    }
  };

  // Handle SQL Training Sync (global - runs in background; poll in background to update status)
  const handleSqlTrainingSync = async () => {
    setIsSyncing(true);
    setSqlSyncingId('global');
    try {
      const response = await fetch(`${API_URL}/api/training/sync/sql-training`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(result.message || 'Sync started in background.', 'success');
        if (result.background) {
          pollSqlTrainingUntilSynced().then(() => {}).catch(() => {});
        } else {
          await fetchSqlTraining();
        }
      } else {
        showToastMessage(result.message || result.detail || 'Sync failed', 'error');
      }
    } catch (error) {
      console.error('Error syncing SQL training:', error);
      showToastMessage('Error syncing SQL training. Please try again.', 'error');
    } finally {
      setIsSyncing(false);
      setSqlSyncingId(null);
    }
  };

  // Handle row-level sync for SQL training (synchronous; refetch so status updates)
  const handleSqlSyncRow = async (id) => {
    setSqlSyncingId(id);
    try {
      const response = await fetch(`${API_URL}/api/training/sync/sql-training/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showToastMessage(result.message || `✓ Synced ${result.trained_count ?? 0} chunks`, 'success');
        await fetchSqlTraining();
      } else {
        showToastMessage(result.message || result.detail || 'Sync failed', 'error');
      }
    } catch (error) {
      console.error('Error syncing SQL row:', error);
      showToastMessage('Error syncing document.', 'error');
    } finally {
      setSqlSyncingId(null);
    }
  };

  // Handle delete SQL training: deletes row, backend resets others is_sync=0, removes sql_index/ metadata, retrains in background and sets is_sync=1 for remaining rows
  const handleDeleteSqlTraining = async (id) => {
    if (!window.confirm('Delete this document? Vanna will retrain with remaining SQL documents in the background.')) return;
    setSqlDeletingId(id);
    try {
      const response = await fetch(`${API_URL}/api/training/sql-training/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showToastMessage(result.message || 'Document deleted. Retraining in background.', 'success');
        setSqlTrainingList((prev) => prev.filter((item) => item.id !== id));
        // Poll in background so remaining rows' status updates to Synced when retrain completes
        pollSqlTrainingUntilSynced().then(() => {}).catch(() => {});
      } else {
        showToastMessage(result.detail || result.message || 'Delete failed', 'error');
      }
    } catch (error) {
      console.error('Error deleting SQL training:', error);
      showToastMessage('Error deleting document.', 'error');
    } finally {
      setSqlDeletingId(null);
    }
  };

  // Handle Business Context submission
  const handleBusinessContextSubmit = async () => {
    if (!businessContextFile && !businessContextText.trim()) {
      showToastMessage('Please provide a file or enter text content', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      
      if (businessContextFile) {
        formData.append('file', businessContextFile);
      }
      
      if (businessContextText.trim()) {
        formData.append('content', businessContextText.trim());
      }

      const response = await fetch(`${API_URL}/api/training/business-context`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const fileName = businessContextFile ? businessContextFile.name : 'text content';
        showToastMessage(`✓ Document uploaded successfully! (${fileName})`, 'success');
        // Clear form and refresh list
        setBusinessContextFile(null);
        setBusinessContextText('');
        if (businessContextFileRef.current) {
          businessContextFileRef.current.value = '';
        }
        fetchBusinessContexts();
        setShowBusinessContextUploadModal(false);
      } else {
        showToastMessage(result.detail || 'Failed to submit business context', 'error');
      }
    } catch (error) {
      console.error('Error submitting business context:', error);
      showToastMessage('Error submitting business context. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle SQL Training submission
  const handleSqlTrainingSubmit = async () => {
    if (!sqlTrainingFile && !sqlTrainingText.trim()) {
      showToastMessage('Please provide a file or enter text content', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      
      if (sqlTrainingFile) {
        formData.append('file', sqlTrainingFile);
      }
      
      if (sqlTrainingText.trim()) {
        formData.append('content', sqlTrainingText.trim());
      }

      const response = await fetch(`${API_URL}/api/training/sql-training`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const fileName = sqlTrainingFile ? sqlTrainingFile.name : 'text content';
        showToastMessage(`✓ SQL training data uploaded successfully! (${fileName})`, 'success');
        setSqlTrainingFile(null);
        setSqlTrainingText('');
        if (sqlTrainingFileRef.current) {
          sqlTrainingFileRef.current.value = '';
        }
        fetchSqlTraining();
        setShowSqlTrainingUploadModal(false);
      } else {
        showToastMessage(result.detail || 'Failed to submit SQL training data', 'error');
      }
    } catch (error) {
      console.error('Error submitting SQL training data:', error);
      showToastMessage('Error submitting SQL training data. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter tables based on status and search
  const filteredTables = tables.filter(table => {
    const matchesStatus = filterStatus === 'all' || table.status === filterStatus;
    const matchesSearch = table.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Get counts
  const mappedCount = tables.filter(t => t.status === 'mapped').length;
  const unmappedCount = tables.filter(t => t.status === 'not_mapped').length;

  // Handle table selection
  const handleSelectTable = (table) => {
    setSelectedTable(table);
    setFormData({
      entityType: table.entityType || '',
      description: table.description || ''
    });
  };

  // Handle save mapping
  const handleSaveMapping = () => {
    if (!selectedTable) return;

    setTables(prev => prev.map(t => 
      t.id === selectedTable.id
        ? {
            ...t,
            entityType: formData.entityType || null,
            description: formData.description,
            status: formData.entityType ? 'mapped' : 'not_mapped'
          }
        : t
    ));

    // Update selected table state
    setSelectedTable(prev => ({
      ...prev,
      entityType: formData.entityType || null,
      description: formData.description,
      status: formData.entityType ? 'mapped' : 'not_mapped'
    }));

    showToastMessage(formData.entityType 
      ? `Table mapped to "${formData.entityType}" successfully!`
      : 'Table mapping removed successfully!',
      'success'
    );
  };

  // Handle clear mapping
  const handleClearMapping = () => {
    setFormData({
      entityType: '',
      description: ''
    });
  };

  // Fetch SQL training list
  const fetchSqlTraining = async () => {
    try {
      const response = await fetch(`${API_URL}/api/training/sql-training`);
      const result = await response.json();
      if (response.ok && result.success) {
        setSqlTrainingList(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching SQL training:', error);
    }
  };

  // Load business context list when Business Context tab is active
  useEffect(() => {
    if (activeSubTab === 'business-context') {
      fetchBusinessContexts();
    }
  }, [activeSubTab]);

  // Load SQL training list when SQL Training tab is active
  useEffect(() => {
    if (activeSubTab === 'sql-training') {
      fetchSqlTraining();
    }
  }, [activeSubTab]);

  // Get status badge color
  const getStatusBadge = (status) => {
    if (status === 'mapped') {
      return 'bg-green-100 text-green-700';
    }
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-90px)] overflow-hidden">
      {/* Sub-tabs */}
      <div className="flex border-b border-slate-200 bg-slate-50 px-4 flex-shrink-0 overflow-x-auto">
        {/* <button
          onClick={() => setActiveSubTab('table-mapping')}
          className={`py-3 px-4 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeSubTab === 'table-mapping'
              ? 'text-indigo-600 border-indigo-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
            <span>Table-to-Domain Mapping</span>
          </div>
        </button> */}
        {/* <button
          onClick={() => setActiveSubTab('column-semantics')}
          className={`py-3 px-4 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeSubTab === 'column-semantics'
              ? 'text-indigo-600 border-indigo-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span>Column Semantics</span>
          </div>
        </button> */}
        <button
          onClick={() => setActiveSubTab('business-context')}
          className={`py-3 px-4 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeSubTab === 'business-context'
              ? 'text-indigo-600 border-indigo-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span>Business Context</span>
          </div>
        </button>
        <button
          onClick={() => setActiveSubTab('sql-training')}
          className={`py-3 px-4 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeSubTab === 'sql-training'
              ? 'text-indigo-600 border-indigo-600'
              : 'text-slate-600 border-transparent hover:text-slate-900'
          }`}
        >
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <span>SQL Training</span>
          </div>
        </button>
      </div>

      {/* Column Semantics Sub-tab */}
      {/* {activeSubTab === 'column-semantics' && (
        <div className="flex-1 overflow-hidden">
          <ColumnSemantics />
        </div>
      )} */}

      {/* Global Toast - Shows on all tabs */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div className={`${toastType === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2`}>
            {toastType === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Table Mapping Sub-tab - COMMENTED OUT */}
      {/* {activeSubTab === 'table-mapping' && (
      <div className="flex flex-1 overflow-hidden">

      {/* Left Panel - Table List */}
      {/* <div className="w-80 flex-shrink-0 border-r border-slate-200 bg-slate-50 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Database Tables</h3>
            <div className="flex items-center space-x-2 text-xs">
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                {mappedCount} mapped
              </span>
              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                {unmappedCount} unmapped
              </span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">Map database tables to business domain entities</p>
        </div>
      </div>
      </div>
      )} */}

      {/* Business Context Tab */}
      {activeSubTab === 'business-context' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 overflow-auto p-6 bg-slate-50">
            {/* Header with Upload and Sync All */}
            <div className="mb-4 flex-shrink-0 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Business Context Training</h2>
                <p className="text-sm text-slate-600">
                  Provide business domain knowledge to help Vanna AI understand your data context better.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowBusinessContextUploadModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload</span>
                </button>
                <button
                  onClick={handleBusinessContextSync}
                  disabled={isSyncing || !businessContextList.some((c) => c.is_sync === 0)}
                  title={businessContextList.some((c) => c.is_sync === 0) ? 'Sync all unsynced documents' : 'No unsynced documents to sync'}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSyncing && syncingId === 'global' ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Sync All</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Business Context Upload Modal */}
            {showBusinessContextUploadModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowBusinessContextUploadModal(false)}>
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">Upload Business Context</h3>
                    <p className="text-sm text-slate-600 mt-1">Upload a document or write business context below.</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="flex flex-col bg-slate-50 rounded-xl border border-slate-200 p-4">
                        <label className="block text-sm font-medium text-slate-700 mb-3">Upload Business Context Document</label>
                        <div
                          className="min-h-[140px] border-2 border-dashed border-slate-300 rounded-xl p-4 flex flex-col items-center justify-center hover:border-indigo-400 transition-colors cursor-pointer"
                          onClick={() => businessContextFileRef.current?.click()}
                        >
                          <input type="file" ref={businessContextFileRef} className="hidden" accept=".txt,.md,.pdf,.doc,.docx" onChange={(e) => setBusinessContextFile(e.target.files[0])} />
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          {businessContextFile ? (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-slate-700">{businessContextFile.name}</span>
                              <button onClick={(e) => { e.stopPropagation(); setBusinessContextFile(null); }} className="text-red-500 hover:text-red-700">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-slate-700">Click to upload</p>
                              <p className="text-xs text-slate-500">TXT, MD, PDF, DOC, DOCX</p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col bg-slate-50 rounded-xl border border-slate-200 p-4">
                        <label className="block text-sm font-medium text-slate-700 mb-3">Write Business Context</label>
                        <textarea
                          value={businessContextText}
                          onChange={(e) => setBusinessContextText(e.target.value)}
                          placeholder="Enter business context here..."
                          className="min-h-[120px] w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm"
                        />
                        <p className="mt-1 text-xs text-slate-500">{businessContextText.length} characters</p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => setShowBusinessContextUploadModal(false)}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => { setBusinessContextText(''); setBusinessContextFile(null); if (businessContextFileRef.current) businessContextFileRef.current.value = ''; }}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                        disabled={isSubmitting}
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleBusinessContextSubmit}
                        disabled={isSubmitting || (!businessContextFile && !businessContextText.trim())}
                        className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            <span>Submit for Training</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Uploaded Business Context Table - scrollable when many rows */}
            <div className="mt-6 flex-shrink-0">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Uploaded Business Context</h3>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden max-h-[40vh] flex flex-col">
                {businessContextList.length === 0 ? (
                  <div className="px-6 py-10 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg m-4">
                    No documents uploaded yet. Use the Upload button to add documents.
                  </div>
                ) : (
                  <div className="overflow-auto flex-1 min-h-0">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Preview</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {businessContextList.map((item) => (
                          <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-slate-900 whitespace-nowrap">
                              {item.file_name || 'Text input'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 max-w-[280px] line-clamp-2" title={item.content_preview || ''}>
                              {item.content_preview || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${item.is_sync === 1 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {item.is_sync === 1 ? 'Synced' : 'Not synced'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2 flex-wrap">
                                {item.is_sync === 0 && (
                                  <button
                                    onClick={() => handleSyncRow(item.id)}
                                    disabled={syncingId !== null}
                                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {syncingId === item.id ? (
                                      <>
                                        <svg className="w-3.5 h-3.5 animate-spin mr-1" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>
                                        Syncing...
                                      </>
                                    ) : (
                                      <>Sync</>
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteBusinessContext(item.id)}
                                  disabled={deletingId !== null}
                                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {deletingId === item.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SQL Training Tab */}
      {activeSubTab === 'sql-training' && (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 overflow-auto p-6 bg-slate-50">
            {/* Header with Upload and Sync All */}
            <div className="mb-4 flex-shrink-0 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">SQL Training Examples</h2>
                <p className="text-sm text-slate-600">
                  Provide example SQL queries to train Vanna AI. Include natural language questions paired with their SQL queries.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSqlTrainingUploadModal(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload</span>
                </button>
                <button
                  onClick={handleSqlTrainingSync}
                  disabled={isSyncing || !sqlTrainingList.some((c) => c.is_sync === 0)}
                  title={sqlTrainingList.some((c) => c.is_sync === 0) ? 'Sync all documents with status Not synced (is_sync=0)' : 'No unsynced documents to sync'}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSyncing && sqlSyncingId === 'global' ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Sync All</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* SQL Training Upload Modal */}
            {showSqlTrainingUploadModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowSqlTrainingUploadModal(false)}>
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-900">Upload SQL Training</h3>
                    <p className="text-sm text-slate-600 mt-1">Upload a file or write SQL training examples below. Use format: Q: question followed by SQL: query</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="flex flex-col bg-slate-50 rounded-xl border border-slate-200 p-4">
                        <label className="block text-sm font-medium text-slate-700 mb-3">Upload SQL Training File</label>
                        <div
                          className="min-h-[140px] border-2 border-dashed border-slate-300 rounded-xl p-4 flex flex-col items-center justify-center hover:border-indigo-400 transition-colors cursor-pointer"
                          onClick={() => sqlTrainingFileRef.current?.click()}
                        >
                          <input type="file" ref={sqlTrainingFileRef} className="hidden" accept=".txt,.sql,.json,.csv" onChange={(e) => setSqlTrainingFile(e.target.files[0])} />
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                          </div>
                          {sqlTrainingFile ? (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-slate-700">{sqlTrainingFile.name}</span>
                              <button onClick={(e) => { e.stopPropagation(); setSqlTrainingFile(null); }} className="text-red-500 hover:text-red-700">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm text-slate-700">Click to upload</p>
                              <p className="text-xs text-slate-500">TXT, SQL, JSON, CSV</p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col bg-slate-50 rounded-xl border border-slate-200 p-4">
                        <label className="block text-sm font-medium text-slate-700 mb-3">Write SQL Training Examples</label>
                        <textarea
                          value={sqlTrainingText}
                          onChange={(e) => setSqlTrainingText(e.target.value)}
                          placeholder="Q: Total PSL outstanding?
SQL: SELECT SUM(i_outstanding_principal) FROM loan_accounts WHERE psl_non_psl = 'PSL';

Q: Top 10 borrowers by amount?
SQL: SELECT i_customer_name, SUM(i_outstanding_principal) as total FROM loan_accounts GROUP BY i_customer_id ORDER BY total DESC LIMIT 10;"
                          className="min-h-[120px] w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm font-mono"
                        />
                        <p className="mt-1 text-xs text-slate-500">{sqlTrainingText.length} characters</p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => setShowSqlTrainingUploadModal(false)}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => { setSqlTrainingText(''); setSqlTrainingFile(null); if (sqlTrainingFileRef.current) sqlTrainingFileRef.current.value = ''; }}
                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                        disabled={isSubmitting}
                      >
                        Clear
                      </button>
                      <button
                        onClick={handleSqlTrainingSubmit}
                        disabled={isSubmitting || (!sqlTrainingFile && !sqlTrainingText.trim())}
                        className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            <span>Submit for Training</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Uploaded SQL Training Table - scrollable when many rows */}
            <div className="mt-6 flex-shrink-0">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Uploaded SQL Training</h3>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden max-h-[40vh] flex flex-col">
                {sqlTrainingList.length === 0 ? (
                  <div className="px-6 py-10 text-center text-slate-500 text-sm border border-dashed border-slate-200 rounded-lg m-4">
                    No SQL training documents yet. Use the Upload button to add documents.
                  </div>
                ) : (
                  <div className="overflow-auto flex-1 min-h-0">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Preview</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider whitespace-nowrap">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {sqlTrainingList.map((item) => (
                          <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-slate-900 whitespace-nowrap">
                              {item.file_name || 'Text input'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600 max-w-[280px] line-clamp-2" title={item.content_preview || ''}>
                              {item.content_preview || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">
                              {item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${item.is_sync === 1 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {item.is_sync === 1 ? 'Synced' : 'Not synced'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2 flex-wrap">
                                {item.is_sync === 0 && (
                                  <button
                                    onClick={() => handleSqlSyncRow(item.id)}
                                    disabled={sqlSyncingId !== null}
                                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {sqlSyncingId === item.id ? (
                                      <>
                                        <svg className="w-3.5 h-3.5 animate-spin mr-1" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                        </svg>
                                        Syncing...
                                      </>
                                    ) : (
                                      <>Sync</>
                                    )}
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteSqlTraining(item.id)}
                                  disabled={sqlDeletingId !== null}
                                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {sqlDeletingId === item.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default SemanticModel;
